import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartOptions,
} from 'chart.js';
import { RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useVoteAggregates } from '@/features/votes/useVoteAggregates';
import type { VoteAggregateAgent } from '@/features/votes/vote-aggregates';
import { useTheme } from '@/hooks/useTheme';
import type { ThemeMode } from '@/lib/theme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

type ThemeTokens = {
  grid: string;
  tick: string;
  label: string;
  upvote: string;
  downvote: string;
};

const TOKENS: Record<ThemeMode, ThemeTokens> = {
  dark: {
    grid: 'rgba(255, 255, 255, 0.07)',
    tick: '#9ca3af',
    label: '#e5e7eb',
    upvote: '#22c55e',
    downvote: '#ef4444',
  },
  light: {
    grid: 'rgba(0, 0, 0, 0.07)',
    tick: '#6b7280',
    label: '#374151',
    upvote: '#16a34a',
    downvote: '#dc2626',
  },
};

function toIsoFromDateTimeLocal(value: string): string {
  return new Date(value).toISOString();
}

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function buildChartOptions(
  rows: VoteAggregateAgent[],
  tokens: ThemeTokens
): ChartOptions<'bar'> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        labels: {
          color: tokens.label,
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const row = rows[ctx.dataIndex];
            if (!row) return '';
            if (ctx.datasetIndex === 0) {
              return `Upvotes: ${formatCount(row.total_upvotes)} (${formatPercent(row.upvotes_pct_of_all_messages)})`;
            }
            return `Downvotes: ${formatCount(row.total_downvotes)} (${formatPercent(row.downvotes_pct_of_all_messages)})`;
          },
          afterBody: (items) => {
            const row = rows[items[0]?.dataIndex ?? -1];
            return row ? `Messages: ${formatCount(row.total_messages)}` : '';
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: tokens.tick,
          autoSkip: false,
          maxRotation: 55,
          minRotation: 0,
          callback(value) {
            const label = this.getLabelForValue(Number(value));
            return label.length > 18 ? `${label.slice(0, 16)}...` : label;
          },
        },
      },
      y: {
        grid: {
          color: tokens.grid,
        },
        ticks: {
          color: tokens.tick,
          callback: (value) => `${Math.abs(Number(value)).toFixed(1)}%`,
        },
      },
    },
  };
}

export default function VoteAggregatesPage() {
  const navigate = useNavigate({ from: '/vote-aggregates' });
  const search = useSearch({ from: '/vote-aggregates' });
  const [startInput, setStartInput] = useState(search.start);
  const [endInput, setEndInput] = useState(search.end);
  const [hiddenAgents, setHiddenAgents] = useState<Set<string>>(new Set());
  const theme = useTheme();
  const { data, isLoading, isFetching, isError, error, refetch } =
    useVoteAggregates(
      toIsoFromDateTimeLocal(search.start),
      toIsoFromDateTimeLocal(search.end)
    );

  useEffect(() => {
    setStartInput(search.start);
    setEndInput(search.end);
    setHiddenAgents(new Set());
  }, [search.end, search.start]);

  const rows = useMemo(
    () =>
      data?.aggregates_per_agent.filter(
        (row) => row.total_upvotes + row.total_downvotes > 0
      ) ?? [],
    [data?.aggregates_per_agent]
  );
  const graphRows = useMemo(
    () => rows.filter((row) => !hiddenAgents.has(row.agent)),
    [hiddenAgents, rows]
  );
  const hiddenRowCount = rows.length - graphRows.length;
  const allAgentsSelected = rows.length > 0 && hiddenRowCount === 0;
  const someAgentsSelected = hiddenRowCount > 0 && hiddenRowCount < rows.length;

  // Native checkbox indeterminate state is only available as a DOM property, so sync it through a ref
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someAgentsSelected;
    }
  }, [someAgentsSelected]);

  const chartData = useMemo(
    () => ({
      labels: graphRows.map((row) => row.agent),
      datasets: [
        {
          label: 'Upvotes',
          data: graphRows.map((row) => row.upvotes_pct_of_all_messages),
          backgroundColor: TOKENS[theme].upvote,
          borderRadius: 3,
        },
        {
          label: 'Downvotes',
          data: graphRows.map((row) => -row.downvotes_pct_of_all_messages),
          backgroundColor: TOKENS[theme].downvote,
          borderRadius: 3,
        },
      ],
    }),
    [graphRows, theme]
  );
  const chartOptions = useMemo(
    () => buildChartOptions(graphRows, TOKENS[theme]),
    [graphRows, theme]
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1.5">
              <CardTitle>Vote Aggregates</CardTitle>
              <CardDescription>
                Message vote rates by agent, sorted by total messages.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="space-y-1.5 text-sm font-medium">
                <span>Start</span>
                <Input
                  type="datetime-local"
                  value={startInput}
                  onChange={(event) => setStartInput(event.target.value)}
                  className="w-full sm:w-56"
                />
              </label>
              <label className="space-y-1.5 text-sm font-medium">
                <span>End</span>
                <Input
                  type="datetime-local"
                  value={endInput}
                  onChange={(event) => setEndInput(event.target.value)}
                  className="w-full sm:w-56"
                />
              </label>
              <Button
                onClick={() =>
                  navigate({
                    search: {
                      start: startInput,
                      end: endInput,
                    },
                  })
                }
              >
                Apply
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => void refetch()}
                aria-label="Refresh vote aggregates"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            The selected time range filters votes by when they were cast, not
            when the voted messages were sent. Message totals use message send
            time.
          </p>
          {isError ? (
            <div className="rounded-md border border-destructive bg-card px-4 py-3 text-sm text-destructive">
              {error instanceof Error
                ? error.message
                : 'Failed to fetch vote aggregates'}
            </div>
          ) : (
            <div className="relative h-[28rem]">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  Loading vote aggregates...
                </div>
              ) : rows.length === 0 ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  No vote aggregates found
                </div>
              ) : graphRows.length === 0 ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  Select at least one agent to show on the graph
                </div>
              ) : (
                <Bar data={chartData} options={chartOptions} />
              )}
            </div>
          )}
          {isFetching && !isLoading && (
            <div className="mt-3 text-sm text-muted-foreground">
              Refreshing...
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Agents</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24 text-center">
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allAgentsSelected}
                      disabled={rows.length === 0}
                      onChange={() =>
                        setHiddenAgents(
                          allAgentsSelected
                            ? new Set(rows.map((row) => row.agent))
                            : new Set()
                        )
                      }
                      className="size-4 cursor-pointer accent-primary disabled:cursor-not-allowed"
                      aria-label="Show all agents on graph"
                    />
                    Graph
                  </label>
                </TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Messages</TableHead>
                <TableHead className="text-right">Upvotes</TableHead>
                <TableHead className="text-right">Upvote rate</TableHead>
                <TableHead className="text-right">Downvotes</TableHead>
                <TableHead className="text-right">Downvote rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No vote aggregates found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.agent}>
                    <TableCell className="text-center">
                      <input
                        type="checkbox"
                        checked={!hiddenAgents.has(row.agent)}
                        onChange={(event) =>
                          setHiddenAgents((currentHiddenAgents) => {
                            const nextHiddenAgents = new Set(
                              currentHiddenAgents
                            );
                            if (event.target.checked) {
                              nextHiddenAgents.delete(row.agent);
                            } else {
                              nextHiddenAgents.add(row.agent);
                            }
                            return nextHiddenAgents;
                          })
                        }
                        className="size-4 cursor-pointer accent-primary"
                        aria-label={`Show ${row.agent} on graph`}
                      />
                    </TableCell>
                    <TableCell className="max-w-[24rem] truncate font-medium">
                      {row.agent}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(row.total_messages)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(row.total_upvotes)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPercent(row.upvotes_pct_of_all_messages)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(row.total_downvotes)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPercent(row.downvotes_pct_of_all_messages)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
